import { Map } from "immutable";
import { isNil } from "lodash";

import { immutableRecords } from "../../../types";
import { calcEventInternalLayout } from "./calcEventInternalLayout";
import { expandLayoutSecondPass } from "./expandLayoutSecondPass";
import { getLayoutConstants } from "./getLayoutConstants";

/**
 * Checks whether an item will fit on this specific line (and this line only)
 * @param {array} mostRightPerLine
 * @param {number} lineIndex
 * @param {number} itemLeft
 */
const doesItemFitLine = (mostRightPerLine, lineIndex, itemLeft) =>
  lineIndex >= mostRightPerLine.length || itemLeft >= mostRightPerLine[lineIndex];

/**
 * Identify the highest position an item can fit into, given the number
 * of vertical lines it will fill
 * @param {array} offsets
 * @param {number} itemHeight
 * @param {number} itemLeft
 */
const findCompactLinePlacement = (mostRightPerLine, itemHeightInLines, itemLeft) => {
  let index = 0;
  let foundPlace = false;

  do {
    if (index >= mostRightPerLine.length) {
      // If we have reached the bottom, it can always go here
      foundPlace = true;
    } else if (doesItemFitLine(mostRightPerLine, index, itemLeft)) {
      // The top line fits, but we need to check the rest
      let failIndex = -1;
      // iterate for the height of the item
      for (let j = 1; j < itemHeightInLines && failIndex < 0; j++) {
        // If we find something that won't fit, then it has failed
        if (!doesItemFitLine(mostRightPerLine, index + j, itemLeft)) {
          // Keep track of the index that failed... there is no point checking between
          // index and failIndex at the top position because we know it will fail every
          // time, so we can jump ahead in the search
          failIndex = index + j;
        }
      }
      if (failIndex < 0) {
        foundPlace = true;
      } else {
        index = failIndex;
      }
    } else {
      // Otherwise, if it is too far left to be placed at this line
      // It won't fit here... update index and keep going
      index++;
    }
  } while (!foundPlace);
  return index;
};

const updateMostRightPerLine = (mostRightPerLine, lineIndex, itemHeightInLines, itemRight) => {
  let result = mostRightPerLine.slice();
  const lastLine = lineIndex + itemHeightInLines;
  const lastExistingLine = Math.min(result.length, lastLine);
  for (let i = lineIndex; i < lastExistingLine; i++) {
    result[i] = itemRight;
  }
  for (let i = lastExistingLine; i < lastLine; i++) {
    result.push(itemRight);
  }
  return result;
};

/**
 * The Compact layout places an event at the highest point that it can fit in its entirety
 * This needs to be used within a two-pass algorithm, since expanded events need to expand
 * from their preferred position, rather than change position as a result of being expanded.
 *
 * This algorithm works by tracking the rightMost position at every vertical line in our layout
 * To work out where to place a node we first calculate how many lines tall it is
 * And then look for a slot where it can fit in every line it needs
 * @param {List} sortedEventIds
 * @param {Map} eventsById
 * @param {Set} ongoingDescendantsSet
 * @param {*} layoutIds
 * @param {*} expandedItemsSet
 * @param {*} unitScale
 * @param {*} startY
 * @param {*} fontSize
 * @param {*} dateForNow
 */
const _calcCompactLayoutForEventList = (
  viewId,
  sortedEventIds,
  eventsById,
  ongoingDescendantsSet,
  layoutIds,
  expandedItemsSet,
  unitScale,
  startY,
  fontSize,
  dateForNow,
  showItemCard
) => {
  const layoutConstants = getLayoutConstants(fontSize, showItemCard);

  let lastBottom = null,
    mostLeft = null,
    mostRight = null,
    mostBottom = startY;

  let mostRightPerLine = [];

  let positions = {};

  const layoutIdsSet = layoutIds.toSet();

  const eventsToLayout = sortedEventIds.filter(id => layoutIdsSet.has(id));

  eventsToLayout.forEach(evId => {
    const ev = eventsById.get(evId);
    const hasOngoingDescendants = ongoingDescendantsSet.has(ev.id);

    // Work out how this event will be laid out
    let position = calcEventInternalLayout(
      viewId,
      ev,
      unitScale,
      fontSize,
      dateForNow,
      hasOngoingDescendants,
      showItemCard
    );
    // And therefore what its height is
    let heightInLines = Math.ceil(position.totalRect.height / layoutConstants.LAYOUT_LINE_HEIGHT);
    // And calculate where it will be placed.
    const linePlacement = findCompactLinePlacement(mostRightPerLine, heightInLines, position.totalRect.left);
    const top = startY + linePlacement * layoutConstants.LAYOUT_LINE_HEIGHT;
    // Track where we placed this item so that it becomes the start position for any children
    lastBottom = top + position.totalRect.height;
    // Update our offsets for the next iteration
    mostRightPerLine = updateMostRightPerLine(mostRightPerLine, linePlacement, heightInLines, position.totalRect.right);

    // Extend when the current event exceeding the most range
    if (mostLeft === null || position.totalRect.left < mostLeft) {
      mostLeft = position.totalRect.left;
    }
    if (mostRight === null || position.totalRect.right > mostRight) {
      mostRight = Math.round(position.totalRect.right);
    }
    if (lastBottom > mostBottom) {
      mostBottom = lastBottom;
    }

    positions[ev.id] = position.setIn(["totalRect", "top"], top).setIn(["totalRect", "bottom"], lastBottom);
  });

  if (mostLeft === null) mostLeft = 0;
  if (mostRight === null) mostRight = 0;

  return {
    positions,
    bounds: immutableRecords.FlatRectRecord({
      left: mostLeft,
      right: mostRight,
      top: startY,
      bottom: mostBottom,
      width: mostRight - mostLeft,
      height: mostBottom - startY
    })
  };
};

const _calcNodeLayoutCompact = (
  viewId,
  sortedEventIds,
  eventsById,
  ongoingDescendantsSet,
  thisLevelItems,
  expandedItemsSet,
  unitScale,
  startY,
  fontSize,
  dateForNow,
  showItemCard
) => {
  let result = _calcCompactLayoutForEventList(
    viewId,
    sortedEventIds,
    eventsById,
    ongoingDescendantsSet,
    thisLevelItems,
    expandedItemsSet,
    unitScale,
    startY,
    fontSize,
    dateForNow,
    showItemCard
  );
  result = expandLayoutSecondPass(
    viewId,
    result,
    _calcNodeLayoutCompact,
    sortedEventIds,
    eventsById,
    ongoingDescendantsSet,
    thisLevelItems,
    expandedItemsSet,
    unitScale,
    startY,
    fontSize,
    dateForNow,
    showItemCard
  );
  return result;
};

export const calcNodeLayoutCompact = (
  viewId,
  data,
  ongoingDescendantsSet,
  topLevelItems,
  expandedItems,
  unitScale,
  fontSize,
  dateForNow,
  showItemCard
) => {
  const expandedItemsSet = expandedItems.toSet();
  const layoutConstants = getLayoutConstants(fontSize, showItemCard);
  const topLevelSet = topLevelItems.toSet();

  const top = 0;
  let groupTop = 0;

  let minLeft = null;
  let maxRight = null;

  const groups = data.groups.map(group => {
    const visibleTopLevelItems = group.eventIds.filter(id => topLevelSet.has(id));

    const thisGroupTop = groupTop;

    if (group.showHeader) {
      groupTop += layoutConstants.GROUP_HEADER_HEIGHT;
    }
    groupTop += layoutConstants.GROUP_TOP_PADDING;

    const result = _calcNodeLayoutCompact(
      viewId,
      group.eventIds,
      data.eventsById,
      ongoingDescendantsSet,
      visibleTopLevelItems,
      expandedItemsSet,
      unitScale,
      groupTop,
      fontSize,
      dateForNow,
      showItemCard
    );

    if (result.bounds.width > 0) {
      if (isNil(minLeft) || result.bounds.left < minLeft) {
        minLeft = result.bounds.left;
      }
      if (isNil(maxRight) || result.bounds.right > maxRight) {
        maxRight = result.bounds.right;
      }
    }

    const groupBottom = result.bounds.bottom + layoutConstants.TIMELINE_BOTTOM_PADDING;

    const groupBounds = result.bounds.merge({
      top: thisGroupTop,
      bottom: groupBottom,
      height: groupBottom - thisGroupTop
    });

    groupTop = groupBottom;

    return group.merge({
      bounds: groupBounds,
      positions: Map(result.positions),
      headerHeight: layoutConstants.GROUP_HEADER_HEIGHT
    });
  });

  const bounds = immutableRecords.FlatRectRecord({
    top,
    bottom: groupTop,
    height: groupTop,
    left: minLeft,
    right: maxRight,
    width: !isNil(minLeft) ? maxRight - minLeft : 0
  });

  return data.merge({
    groups,
    unitScale,
    bounds
  });
};
